import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Package, ArrowRight, XCircle, Loader } from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../utils/formatters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { token, isAuthenticated } = useAuth();
  const { fetchCart, clearCart } = useCart();
  
  const [status, setStatus] = useState("loading");
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, { headers });

      setPaymentStatus(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        if (isAuthenticated) {
          fetchCart();
        } else {
          clearCart();
        }
        if (window.fbq) {
          window.fbq('track', 'Purchase', {
            value: response.data.amount_total / 100,
            currency: response.data.currency?.toUpperCase() || 'AUD',
          });
        }
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 flex items-center justify-center" data-testid="checkout-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg w-full text-center"
      >
        {status === "loading" && (
          <div data-testid="payment-loading">
            <Loader className="w-20 h-20 mx-auto text-[#FFD700] mb-6 animate-spin" />
            <h1 className="font-heading text-4xl mb-4">PROCESSING PAYMENT</h1>
            <p className="text-[#A3A3A3]">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="payment-success">
            <div className="w-24 h-24 bg-[#FFD700] mx-auto mb-8 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-black" />
            </div>
            <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-2">
              ORDER CONFIRMED
            </p>
            <h1 className="font-heading text-5xl md:text-6xl mb-6">
              THANK YOU!
            </h1>
            <p className="text-[#A3A3A3] text-lg mb-8">
              Your order has been placed successfully. You'll receive a confirmation email shortly.
            </p>
            
            {paymentStatus && (
              <div className="border border-white/10 p-6 mb-8 text-left">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-[#A3A3A3]">Amount Paid</span>
                  <span className="font-mono text-[#FFD700] text-xl">
                    {formatPrice(paymentStatus.amount_total / 100)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#A3A3A3]">Payment Status</span>
                  <span className="font-mono text-green-500 uppercase">
                    {paymentStatus.payment_status}
                  </span>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/orders"
                className="btn-primary inline-flex items-center justify-center gap-3"
                data-testid="view-orders-btn"
              >
                <Package className="w-5 h-5" />
                View Orders
              </Link>
              <Link
                to="/shop"
                className="btn-outline inline-flex items-center justify-center gap-3"
                data-testid="continue-shopping-btn"
              >
                Continue Shopping
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        )}

        {status === "expired" && (
          <div data-testid="payment-expired">
            <div className="w-24 h-24 bg-red-500 mx-auto mb-8 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-white" />
            </div>
            <h1 className="font-heading text-4xl mb-4">SESSION EXPIRED</h1>
            <p className="text-[#A3A3A3] mb-8">
              Your payment session has expired. Please try again.
            </p>
            <Link to="/cart" className="btn-primary inline-flex items-center gap-3">
              Return to Cart
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        )}

        {status === "timeout" && (
          <div data-testid="payment-timeout">
            <div className="w-24 h-24 bg-[#FFD700] mx-auto mb-8 flex items-center justify-center">
              <Package className="w-12 h-12 text-black" />
            </div>
            <h1 className="font-heading text-4xl mb-4">CHECKING STATUS</h1>
            <p className="text-[#A3A3A3] mb-8">
              We couldn't confirm your payment status. Please check your orders page for updates.
            </p>
            <Link to="/orders" className="btn-primary inline-flex items-center gap-3">
              View Orders
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
